import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, LogOut, Loader2, CheckCircle2, Circle, Send, Paperclip } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { http, errorText } from "@/lib/adminApi";
import { QuestionsPanel } from "@/pages/admin/QuestionsPanel";
import { RushCountdown } from "@/pages/admin/RushCountdown";
import { ReviewsPanel } from "@/pages/admin/ReviewsPanel";
import { PromosPanel } from "@/pages/admin/PromosPanel";
import { StatsBar } from "@/pages/admin/StatsBar";
import { TipsPanel, SubscribersPanel } from "@/pages/admin/GrowthPanels";
import { OfferSender } from "@/pages/admin/OfferSender";

const field = "w-full rounded-xl border border-white/15 bg-[var(--ink)] px-4 py-3 text-white focus:border-[var(--teal)] focus:outline-none";

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try { await http.post("/auth/login", { email, password }); onDone(); }
    catch (err) { setError(errorText(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="mx-auto mt-24 w-full max-w-sm space-y-4" data-testid="admin-login-form">
      <h1 className="font-serif-d text-5xl text-white">Owner sign in</h1>
      <input type="email" required placeholder="Email" className={field} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
      <input type="password" required placeholder="Password" className={field} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
      {error && <p className="text-sm text-[var(--amber)]" data-testid="admin-login-error">{error}</p>}
      <button disabled={busy} className="w-full rounded-full bg-[var(--teal)] py-3.5 font-semibold text-[var(--ink)] disabled:opacity-70" data-testid="admin-login-button">
        {busy ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
};

const download = async (o) => {
  const res = await http.get(`/admin/orders/${o.id}/resume`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = o.resume_filename || "resume";
  a.click();
  URL.revokeObjectURL(url);
};

const Badge = ({ status }) => (
  <span className={`font-mono-d rounded-full px-2.5 py-1 text-xs uppercase tracking-wider ${status === "paid" ? "bg-[var(--teal)] text-[var(--ink)]" : "border border-white/20 text-slate-300"}`}>
    {status === "paid" ? "Paid" : status === "refunded" ? "Refunded" : status === "awaiting_transfer" ? "Awaiting bank transfer" : "Not paid"}
  </span>
);

const DeliverPanel = ({ o, onSent }) => {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const send = async () => {
    setBusy(true);
    setMsg("");
    try {
      const body = new FormData();
      files.forEach((f) => body.append("files", f));
      const { data } = await http.post(`/admin/orders/${o.id}/deliver`, body);
      setFiles([]);
      onSent(o.id, data);
    } catch (err) { setMsg(errorText(err)); }
    setBusy(false);
  };
  const sent = Array.isArray(o.deliverables) ? o.deliverables : [];
  return (
    <div className="mt-5 border-t border-white/10 pt-4" data-testid={`admin-deliver-panel-${o.id}`}>
      {o.delivery_sent_at && (
        <p className="mb-3 text-sm text-[var(--teal)]" data-testid={`admin-delivery-sent-${o.id}`}>
          Sent to customer {new Date(o.delivery_sent_at).toLocaleString()}: {sent.map((d) => d.filename).join(", ")}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-dashed border-white/25 px-4 py-2 text-sm text-slate-200 hover:border-[var(--teal)]">
          <Paperclip className="h-4 w-4" />
          {files.length ? `${files.length} file${files.length > 1 ? "s" : ""} ready` : "Attach finished Word + PDF"}
          <input type="file" multiple accept=".pdf,.doc,.docx" className="sr-only" data-testid={`admin-deliver-files-${o.id}`}
            onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 4))} />
        </label>
        <button onClick={send} disabled={!files.length || busy} data-testid={`admin-deliver-send-${o.id}`}
          className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[var(--ink)] transition-opacity disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Email to customer
        </button>
      </div>
      {files.length > 0 && <p className="mt-2 truncate text-xs text-slate-400">{files.map((f) => f.name).join(", ")}</p>}
      {msg && <p className="mt-2 text-sm text-[var(--amber)]" role="alert">{msg}</p>}
    </div>
  );
};

const OrderCard = ({ o, onToggle, onSent, onUpdate }) => (
  <li className={`rounded-2xl border p-5 transition-colors sm:p-6 ${o.delivered ? "border-white/5 bg-[var(--navy)]/50" : "border-white/10 bg-[var(--navy)]"}`} data-testid={`admin-order-${o.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-lg font-semibold text-white">{o.name} <span className="font-normal text-slate-400">· {o.tier} ${o.price + (o.rush_price || 0)}</span></p>
      <div className="flex flex-wrap gap-2">
        {o.rush && <RushCountdown o={o} />}
        <Badge status={o.payment_status} />
      </div>
    </div>
    <p className="mt-2 text-sm text-slate-300"><a className="hover:text-[var(--teal)]" href={`mailto:${o.email}`}>{o.email}</a> · {new Date(o.created_at).toLocaleString()}</p>
    <p className="mt-3 text-sm text-white"><span className="text-slate-400">Target job:</span> {o.target_job}</p>
    {(o.referral_code || o.referred_by || o.promo_code_used) && (
      <p className="mt-1 text-sm text-slate-300" data-testid={`admin-referral-${o.id}`}>
        {o.amount_paid != null && <>Paid ${Number(o.amount_paid).toFixed(2)} · </>}
        {o.promo_code_used && <>Used code <span className="font-mono-d text-[var(--teal)]">{o.promo_code_used}</span> · </>}
        {o.referred_by && <>Referred by {o.referred_by} · </>}
        {o.referral_code && <>Their code <span className="font-mono-d text-[var(--teal)]">{o.referral_code}</span>{o.referrals_count ? ` · ${o.referrals_count} friend${o.referrals_count > 1 ? "s" : ""} referred` : ""}</>}
      </p>
    )}
    {o.notes && <p className="mt-1 whitespace-pre-wrap text-sm text-slate-300"><span className="text-slate-400">Notes:</span> {o.notes}</p>}
    <div className="mt-4 flex flex-wrap gap-3">
      <button onClick={() => download(o)} data-testid={`admin-download-${o.id}`}
        className="inline-flex max-w-full items-center gap-2 rounded-full border border-[var(--teal)]/50 px-4 py-2 text-sm text-[var(--teal)] transition-colors hover:bg-[var(--teal)] hover:text-[var(--ink)]">
        <Download className="h-4 w-4 shrink-0" /> <span className="truncate">{o.resume_filename}</span>
      </button>
      {o.payment_status === "paid" && (
        <button onClick={() => onToggle(o)} data-testid={`admin-deliver-${o.id}`}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${o.delivered ? "border border-white/15 text-slate-300 hover:text-white" : "bg-[var(--teal)] text-[var(--ink)] hover:opacity-90"}`}>
          {o.delivered ? <><CheckCircle2 className="h-4 w-4" /> Delivered · undo</> : <><Circle className="h-4 w-4" /> Mark delivered</>}
        </button>
      )}
    </div>
    {o.payment_status === "paid" && <QuestionsPanel o={o} onUpdate={onUpdate} />}
    {o.payment_status === "paid" && <DeliverPanel o={o} onSent={onSent} />}
  </li>
);

const FILTERS = [
  ["todo", "To do", (o) => o.payment_status === "paid" && !o.delivered],
  ["delivered", "Delivered", (o) => o.payment_status === "paid" && o.delivered],
  ["unpaid", "Unpaid", (o) => o.payment_status !== "paid"],
];

export default function Admin() {
  const [state, setState] = useState("checking");
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("todo");
  const [view, setView] = useState("orders");
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await http.get("/admin/orders");
      setOrders(Array.isArray(data?.orders) ? data.orders : []);
      setState("in");
    } catch { setState("out"); }
  };
  useEffect(() => { load(); }, []);

  const logout = async () => { await http.post("/auth/logout"); setState("out"); navigate("/admin"); };
  const toggle = async (o) => {
    const delivered = !o.delivered;
    setOrders((list) => list.map((x) => (x.id === o.id ? { ...x, delivered } : x)));
    try { await http.patch(`/admin/orders/${o.id}/delivered`, { delivered }); }
    catch { setOrders((list) => list.map((x) => (x.id === o.id ? { ...x, delivered: o.delivered } : x))); }
  };
  const onUpdate = (id, patch) => setOrders((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const onSent = (id, data) => setOrders((list) => list.map((x) => (x.id === id
    ? { ...x, delivered: true, delivery_sent_at: data?.delivery_sent_at, deliverables: [...(x.deliverables || []), ...(data?.files || []).map((filename) => ({ filename }))] }
    : x)));
  const due = (o) => (o.rush && o.paid_at ? new Date(o.paid_at).getTime() : Infinity);
  const shown = orders.filter(FILTERS.find((f) => f[0] === filter)[2]).sort((a, b) => (due(a) === due(b) ? 0 : due(a) - due(b)));

  return (
    <div className="min-h-screen bg-[var(--ink)] px-5 py-8 sm:px-10" data-testid="admin-page">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between">
          <Logo />
          {state === "in" && <button onClick={logout} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /> Sign out</button>}
        </div>
        {state === "checking" && <Loader2 className="mx-auto mt-24 h-6 w-6 animate-spin text-[var(--teal)]" />}
        {state === "out" && <Login onDone={load} />}
        {state === "in" && (
          <div className="mt-10 flex gap-6 overflow-x-auto whitespace-nowrap border-b border-white/10">
            {[["orders", "Orders"], ["reviews", "Reviews"], ["promos", "Promo codes"], ["tips", "Weekly tips"], ["subscribers", "Subscribers"], ["offers", "Send offer"]].map(([id, label]) => (
              <button key={id} onClick={() => setView(id)} data-testid={`admin-view-${id}`}
                className={`-mb-px border-b-2 pb-3 text-sm font-semibold transition-colors ${view === id ? "border-[var(--teal)] text-white" : "border-transparent text-slate-400 hover:text-white"}`}>
                {label}
              </button>
            ))}
          </div>
        )}
        {state === "in" && view === "reviews" && <ReviewsPanel />}
        {state === "in" && view === "promos" && <PromosPanel />}
        {state === "in" && view === "tips" && <TipsPanel />}
        {state === "in" && view === "subscribers" && <SubscribersPanel />}
        {state === "in" && view === "offers" && <OfferSender />}
        {state === "in" && view === "orders" && (
          <>
            <StatsBar />
            <div className="mt-10 flex flex-wrap items-end justify-between gap-4">
              <h1 className="font-serif-d text-5xl text-white">Orders</h1>
              <div className="flex gap-1 rounded-full border border-white/10 p-1" role="tablist">
                {FILTERS.map(([id, label, fn]) => (
                  <button key={id} role="tab" aria-selected={filter === id} onClick={() => setFilter(id)} data-testid={`admin-filter-${id}`}
                    className={`rounded-full px-4 py-2 text-sm transition-colors ${filter === id ? "bg-[var(--teal)] font-semibold text-[var(--ink)]" : "text-slate-300 hover:text-white"}`}>
                    {label} <span className="opacity-70">{orders.filter(fn).length}</span>
                  </button>
                ))}
              </div>
            </div>
            {shown.length === 0
              ? <p className="mt-10 text-slate-300" data-testid="admin-empty">Nothing here right now.</p>
              : <ul className="mt-8 space-y-4">{shown.map((o) => <OrderCard key={o.id} o={o} onToggle={toggle} onSent={onSent} onUpdate={onUpdate} />)}</ul>}
          </>
        )}
      </div>
    </div>
  );
}
